import React from 'react';
import classnames from 'classnames';
import { range } from 'lodash';

import {
  RectangleGrayDash,
  RectangleRedDash,
  CheckMark,
} from 'components/common/Icon';

import styles from './styles.module.scss';

interface IAboveLineRectsProps {
  chartType: 'square' | 'rectangle';
  lessonCompleted: number;
}

interface IBelowLineRectsProps {
  chartType: 'square' | 'rectangle';
  lessonCompleted: number;
  status: string;
}

interface IPentagonProps {
  chartType: 'square' | 'rectangle';
  value: number;
}

interface IRectangleProps {
  chartType: 'square' | 'rectangle';
  variant: string;
}

export interface IWeekAboveProps {
  chartType: 'square' | 'rectangle';
  lessonCompleted: number;
}

export interface IWeekBelowProps {
  shouldShowWeekNumber?: boolean;
  shouldShowTickIcon?: boolean;
  chartType: 'square' | 'rectangle';
  status: string;
  lessonCompleted: number;
  weekNumber: number;
  shouldHighLightWeekNumber: boolean;
}

const MAX_BELOW_LESSON_COMPLETED = 4;
const MAX_ABOVE_LESSON_COMPLETED = 3;

const Rectangle = ({ variant, chartType }: IRectangleProps) => (
  <div
    className={classnames(styles[`${chartType}`], styles[`${variant}`])}
    data-testid={`${variant}-rectangle`}
  />
);

const Pentagon = ({ value, chartType }: IPentagonProps) => (
  <div
    className={styles[`above-pentagon-${chartType}`]}
    data-testid="pentagon"
    aria-hidden
  >
    <div className={styles[`pentagon-arrow-${chartType}`]} />+{value}
  </div>
);

const AboveLineRects = ({
  lessonCompleted,
  chartType,
}: IAboveLineRectsProps) => {
  if (lessonCompleted <= MAX_BELOW_LESSON_COMPLETED) {
    return <Rectangle variant="empty" chartType={chartType} />;
  }
  const overValue = lessonCompleted - MAX_BELOW_LESSON_COMPLETED;
  if (overValue > MAX_ABOVE_LESSON_COMPLETED) {
    return <Pentagon value={overValue} chartType={chartType} />;
  }
  return (
    <div className={styles['above-completed-container']}>
      {range(overValue).map((item) => (
        <Rectangle
          key={`above-completed-${item}`}
          variant="filled"
          chartType={chartType}
        />
      ))}
    </div>
  );
};

const BelowLineRects = ({
  status,
  lessonCompleted,
  chartType,
}: IBelowLineRectsProps) => {
  const belowLineLessonCompleted =
    lessonCompleted >= MAX_BELOW_LESSON_COMPLETED
      ? MAX_BELOW_LESSON_COMPLETED
      : lessonCompleted;
  const belowLineLessonNotCompleted =
    MAX_BELOW_LESSON_COMPLETED - belowLineLessonCompleted;
  const isCurrent = status === 'current';

  return (
    <div>
      {range(belowLineLessonNotCompleted).map((item) => {
        if (isCurrent) {
          return (
            <div
              key={`not-completed-${item}`}
              className={styles[`${chartType}-svg-wrapper`]}
              data-testid="dashed-grey-rectangle"
            >
              <RectangleGrayDash width={chartType === 'square' ? '14' : '28'} />
            </div>
          );
        }

        return (
          <div
            key={`not-completed-${item}`}
            className={styles[`${chartType}-svg-wrapper`]}
            data-testid="dashed-red-rectangle"
          >
            <RectangleRedDash width={chartType === 'square' ? '14' : '28'} />
          </div>
        );
      })}
      {range(belowLineLessonCompleted).map((item) => (
        <Rectangle
          key={`below-completed-${item}`}
          variant="filled"
          chartType={chartType}
        />
      ))}
    </div>
  );
};

export const WeekBelow = ({
  lessonCompleted,
  weekNumber,
  status,
  shouldHighLightWeekNumber,
  shouldShowTickIcon,
  shouldShowWeekNumber,
  chartType,
}: IWeekBelowProps) => {
  return (
    <div className={styles.wrapper} data-testid="week-below">
      {lessonCompleted >= MAX_BELOW_LESSON_COMPLETED && shouldShowTickIcon && (
        <div className={styles['tick-background']} />
      )}
      {status !== 'future' && (
        <BelowLineRects
          status={status}
          lessonCompleted={lessonCompleted}
          chartType={chartType}
        />
      )}
      {lessonCompleted >= MAX_BELOW_LESSON_COMPLETED && shouldShowTickIcon && (
        <div className={styles.tick} data-testid="check-icon">
          <CheckMark />
        </div>
      )}
      <div className={styles[`divider-${chartType}`]} />
      <div
        className={classnames(
          styles[`week-number-${chartType}`],
          shouldHighLightWeekNumber && styles[status],
          !shouldShowWeekNumber && styles['text-hidden'],
        )}
      >
        <div
          className={styles['aria-hidden']}
          style={{ width: chartType === 'square' ? '14px' : '28px' }}
        >
          Week
        </div>
        <div>{` ${weekNumber} `}</div>
        <div
          className={styles['aria-desc-hidden']}
          style={{ width: chartType === 'square' ? '14px' : '28px' }}
        >
          {`${
            lessonCompleted === 0 ? 'no' : lessonCompleted
          } lessons completed`}
        </div>
      </div>
    </div>
  );
};

export const WeekAbove = ({ lessonCompleted, chartType }: IWeekAboveProps) => (
  <div className={styles.wrapper} data-testid="week-above">
    <AboveLineRects lessonCompleted={lessonCompleted} chartType={chartType} />
  </div>
);
